var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { apiFetch } from "./apiFetch";
import { hashObject } from "./hash";
/**
 * Hook to retrieve data Electric Era servers that use Auth0.
 * @param {string} getDataUrl Gets data from an Electric Era server.
 * @param {boolean} autoLoad False to not load automatically. Call `execute` to load.
 * @param {number | undefined} version A version number that can be incremented to force a reload.
 */
export function useData(getDataUrl, autoLoad, version) {
    if (autoLoad === void 0) { autoLoad = true; }
    if (version === void 0) { version = undefined; }
    return useDataInternal(getDataUrl, undefined, autoLoad, version);
}
/**
 * Hook to retrieve data from Electric Era servers using Auth0 with a POST request.
 *
 * @param getDataUrl - The URL endpoint to fetch data from an Electric Era server.
 * @param dataBody - The request body to be sent with the POST request.
 * @param autoLoad - Determines whether data should load automatically.
 *                   If false, data must be manually loaded using the `execute` method.
 * @param version - Optional version number that can be incremented to force a data reload.
 * @returns A data retrieval hook with loading state and execution method.
 *
 * @typeParam TData - The type of data expected to be retrieved from the server.
 */
export function useDataPost(getDataUrl, dataBody, autoLoad, version) {
    if (autoLoad === void 0) { autoLoad = true; }
    if (version === void 0) { version = undefined; }
    return useDataInternal(getDataUrl, dataBody, autoLoad, version);
}
function useDataInternal(dataUrl, dataBody, autoLoad, version) {
    var _this = this;
    if (autoLoad === void 0) { autoLoad = true; }
    if (version === void 0) { version = undefined; }
    var _a = useAuth0(), isLoading = _a.isLoading, getAccessTokenSilently = _a.getAccessTokenSilently;
    var lastAbort = useRef();
    var lastResult = useRef();
    var lastUrl = useRef();
    var lastBodyHash = useRef(undefined);
    var versionRef = useRef(version);
    var autoLoadRef = useRef(autoLoad);
    var _b = useState(true), dataIsLoading = _b[0], setDataIsLoading = _b[1];
    var urlChanged = lastUrl.current !== dataUrl;
    // Wrap fetch to handle asynchronous
    var memoizedExec = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var getToken, args, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setDataIsLoading(true);
                    getToken = function () {
                        return getAccessTokenSilently({
                            authorizationParams: {
                                audience: process.env.AUTH0_AUDIENCE,
                            },
                        });
                    };
                    // Abort the last exec if it's currently executing.
                    if (lastAbort.current) {
                        lastAbort.current.abort("New request for url: ".concat(dataUrl));
                    }
                    lastAbort.current = new AbortController();
                    args = {
                        method: dataBody ? "POST" : "GET",
                        body: dataBody,
                        callbacks: {
                            getToken: getToken,
                        },
                        signal: lastAbort.current.signal,
                    };
                    _a = lastResult;
                    return [4 /*yield*/, apiFetch(dataUrl, args)];
                case 1:
                    _a.current = _b.sent();
                    lastAbort.current = undefined;
                    setDataIsLoading(false);
                    return [2 /*return*/];
            }
        });
    }); }, [
        getAccessTokenSilently,
        dataBody,
        dataUrl,
        lastAbort,
        lastResult,
        setDataIsLoading,
    ]);
    var hasNonBodyChange = useMemo(function () {
        var versionChanged = versionRef.current !== version && version !== undefined;
        var autoLoadNotTriggerred = !autoLoadRef.current && autoLoad;
        return urlChanged || versionChanged || autoLoadNotTriggerred;
    }, [versionRef, version, autoLoadRef, autoLoad, urlChanged]);
    useEffect(function () {
        var checkForChange = function () { return __awaiter(_this, void 0, void 0, function () {
            var currentBodyHash, _a, bodyChanged, err_1;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!dataBody) return [3 /*break*/, 2];
                        return [4 /*yield*/, hashObject(dataBody)];
                    case 1:
                        _a = _c.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _a = undefined;
                        _c.label = 3;
                    case 3:
                        currentBodyHash = _a;
                        bodyChanged = lastBodyHash.current !== currentBodyHash;
                        if (!(hasNonBodyChange ||
                            bodyChanged ||
                            ((_b = lastAbort.current) === null || _b === void 0 ? void 0 : _b.signal.aborted))) return [3 /*break*/, 7];
                        _c.label = 4;
                    case 4:
                        _c.trys.push([4, 6, , 7]);
                        if (version != undefined) {
                            versionRef.current = version;
                        }
                        if (currentBodyHash != undefined) {
                            lastBodyHash.current = currentBodyHash;
                        }
                        lastUrl.current = dataUrl;
                        autoLoadRef.current = autoLoad;
                        return [4 /*yield*/, memoizedExec()];
                    case 5:
                        _c.sent();
                        return [3 /*break*/, 7];
                    case 6:
                        err_1 = _c.sent();
                        console.error(err_1);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        }); };
        void checkForChange();
        return function () {
            var _a;
            (_a = lastAbort.current) === null || _a === void 0 ? void 0 : _a.abort("Abort due to unmount of useData"); // cancel fetch on unmount
        };
    }, [
        autoLoadRef,
        autoLoad,
        versionRef,
        version,
        memoizedExec,
        hasNonBodyChange,
        lastUrl,
        dataUrl,
        lastBodyHash,
        dataBody,
    ]);
    return {
        response: lastResult.current,
        // If autoLoad = false, treat the request as loading until executed.
        isLoading: isLoading || dataIsLoading,
        // Keep returning the last result until we get a new one (such as on `execute`) to
        // reduce flickering and excessive reloading.
        data: lastResult.current,
        execute: memoizedExec,
    };
}
